import { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";

import { Select } from "components/ui";
import { useSubmitLabOrderTemplate } from "features/labOrders/hooks/useSubmitLabOrderTemplate";
import {
  getLabOrderErrorToast,
  LAB_ORDER_ERROR_KIND,
  mapLabOrderError,
} from "features/labOrders/utils/labOrderErrors";
import {
  buildHospitalGroups,
  buildHospitalOptions,
  buildLaboratoryOptions,
  findFacility,
  hasLaboratoryChoice,
} from "features/labOrders/utils/labOrderFacilities";
import {
  buildLabOrderIbRedirect,
  IB_REDIRECT_FALLBACK_MS,
  rememberPendingLabOrder,
} from "features/labOrders/utils/labOrderIbRedirect";
import {
  buildOrderingGpOptions,
  resolveDefaultOrderingGp,
  toOrderingStaffId,
} from "features/labOrders/utils/orderingGps";
import { useAllStaffMembers } from "features/practice/hooks/useAllStaffMembers";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";
import { formatDate } from "utils/dateFormatter";

// The referral flow's own step wrapper, so the two Healthlink flows share the layout.
import { ItemFormWrapper } from "../../../PatientReferralsPage/components/AddReferralForm/components/ItemFormWrapper/ItemFormWrapper";
import s from "./NewLabOrderForm.module.css";

const PatientField = ({ label, value }) => (
  <div className={s.patientField}>
    <dt className={s.patientFieldLabel}>{label}</dt>
    <dd className={s.patientFieldValue}>{value || "-"}</dd>
  </div>
);

// These four fields are the whole of what the practice system transmits about the
// patient (PID), so they are shown as fields rather than as a squashed line: they are
// what the GP is confirming, and what a Healthlink rejection would name.
const PatientSummary = ({ patient }) => {
  if (!patient) return null;

  const address = patient.address ?? {};

  return (
    <section className={s.patient}>
      <h2 className={s.patientHeading}>Patient</h2>
      <dl className={s.patientGrid}>
        <PatientField
          label="Name"
          value={[patient.firstName, patient.surname].filter(Boolean).join(" ")}
        />
        <PatientField
          label="Date of birth"
          value={formatDate(patient.dateOfBirth)}
        />
        <PatientField label="Gender" value={patient.gender} />
        <PatientField
          label="Address"
          value={[
            address.addressLine1,
            address.addressLine2,
            address.city,
            address.county,
            address.postCode,
          ]
            .filter(Boolean)
            .join(", ")}
        />
      </dl>
    </section>
  );
};

// Step 1 of placing a lab order: which patient (fixed) and which hospital
// laboratory. Nothing clinical — the tests, clinical details, specimen type and
// priority are chosen in Healthlink at step 2.
//
// The hospital is required; the laboratory department is offered **only when the
// directory has one**. Every real laboratory-order facility Healthlink published for
// this practice has a blank DepartmentAgencyCode (KIN-1533), which is why a second
// select used to sit there empty and unfillable, blocking the order.
//
// Deliberately *not* the referral flow's group-or-hospital first select: there, a
// "Clinic" field appears only when a group was chosen and vanishes when a hospital is
// picked directly, which makes the form shift under the user for no gain.
export const NewLabOrderForm = ({
  patientId,
  patient,
  facilities,
  areFacilitiesLoading,
  hasFacilitiesError,
  onRetryFacilities,
  isBlockedByPatientDetails,
  onRedirectFailed,
  onBack,
}) => {
  const [hospitalId, setHospitalId] = useState("");
  const [agencyCode, setAgencyCode] = useState("");
  const [orderingGpId, setOrderingGpId] = useState("");

  const { data: staffMembers } = useAllStaffMembers();
  const { data: staffInfo } = useStaffInfo();
  const currentStaffId = staffInfo?.staffMember?.id;

  const orderingGpOptions = buildOrderingGpOptions(staffMembers);

  /*
   * Starts on the signed-in user once their staff record is known, so a GP ordering for
   * their own patient sees no change. A nurse starts on nobody and has to choose: it is
   * her patient's order, but it is not her number that goes on it.
   */
  useEffect(() => {
    setOrderingGpId((chosen) =>
      chosen || resolveDefaultOrderingGp({ staffMembers, currentStaffId })
    );
  }, [staffMembers, currentStaffId]);

  // Until Healthlink confirm whose account may send an order placed for somebody else,
  // the backend answers that case 409. Said here rather than after the attempt.
  const isOrderingForAnotherGp =
    !!orderingGpId && String(orderingGpId) !== String(currentStaffId);

  const { mutate: submitTemplate, isPending: isSubmitting } =
    useSubmitLabOrderTemplate();
  const fallbackTimer = useRef(null);

  useEffect(() => () => clearTimeout(fallbackTimer.current), []);

  const hospitalOptions = buildHospitalOptions(facilities);
  const hospitalGroups = buildHospitalGroups(facilities);
  const laboratoryOptions = buildLaboratoryOptions(facilities, hospitalId);
  const offersLaboratoryChoice = hasLaboratoryChoice(facilities, hospitalId);

  const selectedFacility = findFacility(facilities, hospitalId, agencyCode);
  // A department is required only where one exists to choose.
  const isDestinationChosen =
    !!selectedFacility && (!offersLaboratoryChoice || !!agencyCode);
  const canSubmit =
    isDestinationChosen &&
    !!orderingGpId &&
    !isBlockedByPatientDetails &&
    !isSubmitting;

  const resetDestination = () => {
    setHospitalId("");
    setAgencyCode("");
  };

  // Changing hospital invalidates the laboratory: an agency code belongs to one
  // hospital, so keeping it would leave a destination that does not exist.
  const handleHospitalChange = (value) => {
    setHospitalId(value ? String(value) : "");
    setAgencyCode("");
  };

  const handleSubmit = () => {
    if (!canSubmit) return;

    submitTemplate(
      {
        patientId,
        hospitalId: selectedFacility.hospitalId,
        // Sent only when the facility actually has one — the backend falls back to
        // matching on the hospital alone, and a blank code is normal.
        ...(selectedFacility.agencyCode?.trim()
          ? { agencyCode: selectedFacility.agencyCode }
          : {}),
        orderingStaffId: toOrderingStaffId({ orderingGpId, currentStaffId }),
      },
      {
        // Straight out to Healthlink, as a referral goes: no extra step asking the GP to
        // click through to it. A successful redirect unloads this page; if we are still
        // here a moment later it did not happen, and the page shows the copy-link
        // fallback instead — the same arrangement AddReferralForm uses.
        onSuccess: (submission) => {
          const redirectUrl = buildLabOrderIbRedirect(
            submission,
            window.location.href
          );

          if (!redirectUrl) {
            onRedirectFailed(submission);

            return;
          }

          rememberPendingLabOrder(submission.id);
          window.location.href = redirectUrl;

          clearTimeout(fallbackTimer.current);
          fallbackTimer.current = setTimeout(
            () => onRedirectFailed(submission),
            IB_REDIRECT_FALLBACK_MS
          );
        },
        onError: (error) => {
          const mappedError = mapLabOrderError(error);

          toast.error(getLabOrderErrorToast(mappedError), { duration: 8000 });

          // A stale directory is the one failure the page can act on itself: drop
          // the selection and refetch, so the GP reselects from what still exists.
          if (mappedError.kind === LAB_ORDER_ERROR_KIND.staleFacilities) {
            resetDestination();
            onRetryFacilities?.();
          }
        },
      }
    );
  };

  return (
    <ItemFormWrapper
      onBack={onBack}
      onContinue={handleSubmit}
      continueText={isSubmitting ? "Sending…" : "Continue"}
      disabled={!canSubmit}
    >
      <PatientSummary patient={patient} />

      <div className={`${s.field} ${s.orderingGp}`}>
        <label className={s.label} htmlFor="lab-order-ordering-gp">
          Ordering GP
        </label>
        <Select
          name="lab-order-ordering-gp"
          options={orderingGpOptions}
          value={orderingGpId}
          onChange={(value) => setOrderingGpId(value ? String(value) : "")}
          placeholder={
            orderingGpOptions.length === 0
              ? "No GP has a Medical Council Number recorded"
              : "Select the ordering GP"
          }
          disabled={orderingGpOptions.length === 0}
          fullWidth
        />
        <p className={s.helpText}>
          {isOrderingForAnotherGp
            ? "Placing an order under another GP isn't available yet — sign in as that GP to place it."
            : "The order goes out under this GP's Medical Council Number."}
        </p>
      </div>

      {hasFacilitiesError ? (
        <div className={s.facilitiesProblem}>
          <p className={s.helpText}>
            We couldn&apos;t load the hospital laboratories.
          </p>
          <button
            type="button"
            className={s.retryButton}
            onClick={onRetryFacilities}
          >
            Try again
          </button>
        </div>
      ) : (
        <div className={s.selects}>
          <div className={s.field}>
            <label className={s.label} htmlFor="lab-order-hospital">
              Hospital
            </label>
            <Select
              name="lab-order-hospital"
              options={hospitalGroups ? undefined : hospitalOptions}
              groups={hospitalGroups ?? undefined}
              value={hospitalId}
              onChange={handleHospitalChange}
              placeholder={
                areFacilitiesLoading ? "Loading…" : "Select a hospital"
              }
              disabled={areFacilitiesLoading || hospitalOptions.length === 0}
              fullWidth
            />
          </div>

          {!!offersLaboratoryChoice && (
            <div className={s.field}>
              <label className={s.label} htmlFor="lab-order-laboratory">
                Laboratory
              </label>
              <Select
                name="lab-order-laboratory"
                options={laboratoryOptions}
                value={agencyCode}
                onChange={(value) => setAgencyCode(value ? String(value) : "")}
                placeholder="Select a laboratory"
                fullWidth
              />
            </div>
          )}
        </div>
      )}
    </ItemFormWrapper>
  );
};
